import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { fmt } from 'pages/VocabularyTest/cefr';

interface VocabularyTestActionPlanCardProps {
  currentLevel?: string;
  learnedCount: number;
}

const NEXT_LEVEL_TARGETS: Record<
  string,
  { next: string; threshold: number }
> = {
  A0: { next: 'A1', threshold: 500 },
  A1: { next: 'A2', threshold: 1400 },
  A2: { next: 'B1', threshold: 2100 },
  B1: { next: 'B2', threshold: 3900 },
  B2: { next: 'C1', threshold: 6000 },
  C1: { next: 'C2', threshold: 8600 },
};

const WORDS_PER_WEEK = 50;

const monthsFromWeeks = (weeks: number) =>
  Math.max(1, Math.round(weeks / 4.34));

interface PlanStepData {
  title: string;
  hint: string;
}

const PlanStep: React.FC<{ num: number } & PlanStepData> = ({
  num,
  title,
  hint,
}) => (
  <Stack direction={'row'} spacing={1.5}>
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: (theme) =>
          theme.palette.mode === 'light'
            ? 'rgba(59, 44, 147, 0.10)'
            : 'rgba(155, 124, 255, 0.18)',
        borderRadius: '50%',
        color: 'primary.main',
        display: 'inline-flex',
        flex: 'none',
        fontSize: 13,
        fontWeight: 800,
        height: 26,
        justifyContent: 'center',
        width: 26,
      }}
    >
      {num}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35 }}>
        {title}
      </Typography>
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 13,
          lineHeight: 1.45,
          mt: 0.25,
        }}
      >
        {hint}
      </Typography>
    </Box>
  </Stack>
);

const VocabularyTestActionPlanCard: React.FC<
  VocabularyTestActionPlanCardProps
> = ({ currentLevel, learnedCount }) => {
  if (!currentLevel) {
    return null;
  }

  const target = NEXT_LEVEL_TARGETS[currentLevel];
  const need = target ? Math.max(0, target.threshold - learnedCount) : 0;
  const months = target ? monthsFromWeeks(need / WORDS_PER_WEEK) : 0;

  const steps: PlanStepData[] = [
    target
      ? {
          title: `До рівня ${target.next} — ще ${fmt(need)} слів`,
          hint: `Темп ${WORDS_PER_WEEK} слів/тиждень → орієнтовно ${months} міс.`,
        }
      : {
          title: 'Ти на топ-рівні C2',
          hint: 'Підтримуй форму регулярним читанням і повторенням',
        },
    {
      title: 'Додавай слова зі свого контенту',
      hint: 'У розділі «В дії» зберігай слова із серіалів, книжок і статей, які реально дивишся та читаєш',
    },
    {
      title: 'Повторюй вивчене регулярно',
      hint: 'Короткі сесії повторення закріплюють слова в активному запасі',
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        p: { xs: 2, md: '20px 22px' },
      }}
    >
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'primary.tableBorder',
          mb: 2,
          pb: 1.75,
        }}
      >
        <LightbulbIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
          {'Що далі'}
        </Typography>
      </Stack>

      <Stack spacing={2} sx={{ pl: 0.5 }}>
        {steps.map((s, i) => (
          <PlanStep key={i} num={i + 1} title={s.title} hint={s.hint} />
        ))}
      </Stack>
    </Box>
  );
};

export default VocabularyTestActionPlanCard;
